import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, LinearProgress, IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    LockOutlined, NorthEastOutlined, SouthWestOutlined,
    ShowChartOutlined, PieChartOutlined, BarChartOutlined,
    TrendingUpOutlined, ArrowBackOutlined,
} from '@mui/icons-material';
import {
    AreaChart, Area, LineChart, Line, BarChart, Bar,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { B, PORTFOLIO } from './investData';

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

/* ── Demo data ── */
const PERF_DATA: Record<string, { label: string; value: number }[]> = {
    '1W': [
        { label: 'Mon', value: 278200 }, { label: 'Tue', value: 280100 },
        { label: 'Wed', value: 279400 }, { label: 'Thu', value: 282300 },
        { label: 'Fri', value: 285100 }, { label: 'Sat', value: 284800 },
        { label: 'Sun', value: 287450 },
    ],
    '1M': Array.from({ length: 30 }, (_, i) => ({ label: `${i+1}`, value: 250000 + Math.random()*12000 + i*1300 })),
    '3M': Array.from({ length: 12 }, (_, i) => ({ label: `W${i+1}`, value: 210000 + i*6500 + Math.random()*4000 })),
    '1Y': [
        { label: 'Jan', value: 160000 }, { label: 'Feb', value: 168000 },
        { label: 'Mar', value: 175000 }, { label: 'Apr', value: 171000 },
        { label: 'May', value: 185000 }, { label: 'Jun', value: 192000 },
        { label: 'Jul', value: 204000 }, { label: 'Aug', value: 218000 },
        { label: 'Sep', value: 228000 }, { label: 'Oct', value: 241000 },
        { label: 'Nov', value: 262000 }, { label: 'Dec', value: 287450 },
    ],
};

const BENCHMARK_DATA = [
    { label: 'Portfolio', value: 13.8, color: B.purple },
    { label: 'NGX ASI',   value: 9.4,  color: B.blue   },
    { label: 'Inflation', value: 33.2, color: B.red     },
    { label: 'T-Bills',   value: 18.5, color: B.green   },
];

const MONTHLY_DATA = [
    { month: 'Jul', gain: 8400  }, { month: 'Aug', gain: 12100 },
    { month: 'Sep', gain: 6800  }, { month: 'Oct', gain: 15200 },
    { month: 'Nov', gain: 18700 }, { month: 'Dec', gain: 11450 },
];

const BREAKDOWN = [
    { name: 'Stanbic Money Mkt', type: 'fund',  allocation: 41.7, value: 120000, gain: 12.8, color: B.green  },
    { name: 'CardinalStone Eq.', type: 'fund',  allocation: 30.4, value: 87450,  gain: 38.6, color: B.purple },
    { name: 'DANGCEM',           type: 'stock', allocation: 17.4, value: 50000,  gain: 5.2,  color: '#FF6B35'},
    { name: 'SEPLAT',            type: 'stock', allocation: 10.4, value: 30000,  gain: 12.4, color: B.green  },
];

const PERIODS = ['1W', '1M', '3M', '1Y'];

/* ── Custom tooltip ── */
const DarkTooltip = ({ active, payload, label, isDark }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ background: isDark ? alpha(B.dark, .95) : '#fff', border: `1px solid ${alpha(B.purple, .2)}`, borderRadius: '12px', px: 1.75, py: 1.25, boxShadow: `0 8px 24px ${alpha(B.dark, .3)}` }}>
            <Typography sx={{ color: alpha(isDark?'#fff':B.ink, .45), fontSize: '.65rem', mb: .3 }}>{label}</Typography>
            <Typography sx={{ color: isDark?'#fff':B.ink, fontWeight: 700, fontSize: '.82rem' }}>{fmt(payload[0].value)}</Typography>
        </Box>
    );
};

const PortfolioAnalyticsPage: React.FC = () => {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [period, setPeriod] = useState('1Y');
    const [chartTab, setChartTab] = useState<'performance'|'monthly'|'breakdown'>('performance');

    const navigate  = useNavigate();

    const pageBg  = isDark ? B.deep : B.lavender;
    const surf    = isDark ? alpha(B.mid, .55) : '#fff';
    const bdr     = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const shd     = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri = isDark ? '#fff' : B.ink;
    const textMid = alpha(isDark ? '#fff' : B.ink, .45);
    const textFnt = alpha(isDark ? '#fff' : B.ink, .28);
    const divC    = alpha(isDark ? '#fff' : B.dark, .08);
    const glass   = { background: surf, border: bdr, boxShadow: shd, borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none' };

    const perfData  = PERF_DATA[period];
    const firstVal  = perfData[0].value;
    const lastVal   = perfData[perfData.length-1].value;
    const periodGain = lastVal - firstVal;
    const periodGainPct = ((periodGain / firstVal) * 100).toFixed(2);
    const isPositive    = periodGain >= 0;

    return (
        <Box sx={{ minHeight:'100vh', background:pageBg, pb:12, fontFamily:"'DM Sans',system-ui,sans-serif" }}>

            {/* Hero */}
            <Box sx={{ background:`linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`, mt:{ xs:'-76px', sm:0 }, pt:{ xs:'80px', sm:'28px' }, pb:{ xs:4, sm:16 }, px:3, position:'relative', overflow:'hidden' }}>
                <Box sx={{ position:'absolute', top:-60, right:-40, width:220, height:220, borderRadius:'50%', background:`radial-gradient(circle, ${alpha(B.green,.1)} 0%, transparent 70%)`, pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', bottom:-10, left:-40, width:180, height:180, borderRadius:'50%', background:`radial-gradient(circle, ${alpha(B.purple,.18)} 0%, transparent 70%)`, pointerEvents:'none' }} />
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <IconButton size="small" onClick={() => navigate(-1)} sx={{ color:alpha('#fff',.6), background:alpha('#fff',.08), '&:hover':{ background:alpha('#fff',.16) } }}>
                        <ArrowBackOutlined sx={{ fontSize:18 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ color:alpha('#fff',.35), fontSize:'.62rem', letterSpacing:'.18em', textTransform:'uppercase' }}>NG Credit</Typography>
                        <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'1.15rem', fontFamily:"'Instrument Serif',serif" }}>Portfolio Analytics</Typography>
                    </Box>
                </Box>
                <Typography sx={{ color:alpha('#fff',.5), fontSize:'.68rem', letterSpacing:'.12em', textTransform:'uppercase', mb:.5 }}>Total portfolio value</Typography>
                <Typography sx={{ color:'#fff', fontFamily:"'Instrument Serif',serif", fontSize:'2.4rem', lineHeight:1 }}>{fmt(PORTFOLIO.totalValue)}</Typography>
                <Box sx={{ display:'inline-flex', alignItems:'center', gap:.5, mt:1, background:alpha(B.green,.15), border:`1px solid ${alpha(B.green,.25)}`, borderRadius:20, px:1.25, py:.4 }}>
                    <NorthEastOutlined sx={{ color:B.green, fontSize:12 }} />
                    <Typography sx={{ color:B.green, fontSize:'.72rem', fontWeight:700 }}>+{PORTFOLIO.gainPct}% all-time · +{fmt(PORTFOLIO.totalGain)}</Typography>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt:{ xs:2, sm:-10 }, position:'relative', zIndex:1, px:{ xs:2, sm:3 } }}>

                {/* Stat strip */}
                <Box sx={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:1.25, mb:3 }}>
                    {[
                        { label:'Total invested', value:fmt(252630).slice(0,9), color:textPri },
                        { label:'Today\'s gain',  value:'+₦1,240',             color:B.green },
                        { label:'Best holding',   value:'+38.6%',              color:B.green },
                    ].map(s => (
                        <Box key={s.label} sx={{ ...glass, p:1.75, textAlign:'center' }}>
                            <Typography sx={{ color:s.color, fontFamily:"'Instrument Serif',serif", fontSize:'.95rem', lineHeight:1.2 }}>{s.value}</Typography>
                            <Typography sx={{ color:textFnt, fontSize:'.6rem', mt:.3 }}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Chart tab strip */}
                <Box sx={{ ...glass, p:.7, mb:2, display:'flex', gap:.5 }}>
                    {([
                        { key:'performance' as const, label:'Performance', Icon:ShowChartOutlined },
                        { key:'monthly'     as const, label:'Monthly',     Icon:BarChartOutlined  },
                        { key:'breakdown'   as const, label:'Breakdown',   Icon:PieChartOutlined  },
                    ]).map(({ key, label, Icon }) => {
                        const active = chartTab === key;
                        return (
                            <Box key={key} onClick={() => setChartTab(key)} sx={{ flex:1, py:1, borderRadius:'14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:.5, background: active ? `linear-gradient(135deg,${B.purple},${B.light})` : 'transparent', boxShadow: active ? `0 4px 16px ${alpha(B.purple,.32)}` : 'none', transition:'all .22s' }}>
                                <Icon sx={{ fontSize:13, color:active?'#fff':textFnt }} />
                                <Typography sx={{ color:active?'#fff':textFnt, fontWeight:700, fontSize:'.7rem' }}>{label}</Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Performance chart ── */}
                {chartTab==='performance' && (
                    <Box sx={{ ...glass, p:2.5, mb:2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box>
                                <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.9rem' }}>Growth</Typography>
                                <Box display="flex" alignItems="center" gap={.75} mt={.5}>
                                    {isPositive ? <NorthEastOutlined sx={{ color:B.green, fontSize:14 }} /> : <SouthWestOutlined sx={{ color:B.red, fontSize:14 }} />}
                                    <Typography sx={{ color:isPositive?B.green:B.red, fontWeight:700, fontSize:'.8rem' }}>
                                        {isPositive?'+':''}{fmt(periodGain)} ({isPositive?'+':''}{periodGainPct}%)
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display:'flex', gap:.6 }}>
                                {PERIODS.map(p => (
                                    <Box key={p} onClick={() => setPeriod(p)} sx={{ px:1, py:.4, borderRadius:8, cursor:'pointer', background: period===p ? alpha(B.purple,.15) : 'transparent', border:`1px solid ${period===p ? alpha(B.purple,.3) : 'transparent'}`, transition:'all .15s' }}>
                                        <Typography sx={{ color: period===p ? B.purple : textFnt, fontWeight:700, fontSize:'.7rem' }}>{p}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <ResponsiveContainer width="100%" height={180}>
                            <AreaChart data={perfData} margin={{ top:4, right:4, left:4, bottom:0 }}>
                                <defs>
                                    <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%"  stopColor={B.purple} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={B.purple} stopOpacity={0}   />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={alpha(isDark?'#fff':B.dark,.05)} vertical={false} />
                                <XAxis dataKey="label" tick={{ fill:alpha(isDark?'#fff':B.ink,.3), fontSize:10 }} axisLine={false} tickLine={false} />
                                <YAxis hide />
                                <Tooltip content={<DarkTooltip isDark={isDark} />} />
                                <Area type="monotone" dataKey="value" stroke={B.purple} strokeWidth={2} fill="url(#perfGrad)" dot={false} activeDot={{ r:5, fill:B.purple, strokeWidth:2, stroke:isDark?B.deep:'#fff' }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                )}

                {/* ── Monthly returns chart ── */}
                {chartTab==='monthly' && (
                    <Box sx={{ ...glass, p:2.5, mb:2 }}>
                        <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.9rem', mb:2 }}>Monthly Returns</Typography>
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={MONTHLY_DATA} margin={{ top:4, right:4, left:4, bottom:0 }}>
                                <defs>
                                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%"   stopColor={B.purple} stopOpacity={1} />
                                        <stop offset="100%" stopColor={B.light}  stopOpacity={0.6} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={alpha(isDark?'#fff':B.dark,.05)} vertical={false} />
                                <XAxis dataKey="month" tick={{ fill:alpha(isDark?'#fff':B.ink,.3), fontSize:10 }} axisLine={false} tickLine={false} />
                                <YAxis hide />
                                <Tooltip
                                    contentStyle={{ background: isDark ? alpha(B.dark,.95) : '#fff', border:`1px solid ${alpha(B.purple,.2)}`, borderRadius:'12px', boxShadow:`0 8px 24px ${alpha(B.dark,.3)}` }}
                                    labelStyle={{ color: alpha(isDark?'#fff':B.ink,.45), fontSize:'.65rem' }}
                                    itemStyle={{ color: isDark?'#fff':B.ink, fontWeight:700, fontSize:'.82rem' }}
                                    formatter={(v: number) => [fmt(v), 'Gain']}
                                />
                                <Bar dataKey="gain" fill="url(#barGrad)" radius={[6,6,0,0]} />
                            </BarChart>
                        </ResponsiveContainer>
                        <Box display="flex" justifyContent="space-between" mt={1.5} pt={1.5} sx={{ borderTop:`1px solid ${divC}` }}>
                            <Box>
                                <Typography sx={{ color:textFnt, fontSize:'.62rem' }}>Best month</Typography>
                                <Typography sx={{ color:B.green, fontWeight:700, fontSize:'.8rem' }}>{fmt(18700)} (Nov)</Typography>
                            </Box>
                            <Box sx={{ textAlign:'right' }}>
                                <Typography sx={{ color:textFnt, fontSize:'.62rem' }}>6-month total</Typography>
                                <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.8rem' }}>{fmt(MONTHLY_DATA.reduce((s,d)=>s+d.gain,0))}</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* ── Breakdown ── */}
                {chartTab==='breakdown' && (
                    <Box sx={{ ...glass, p:2.5, mb:2 }}>
                        <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.9rem', mb:2 }}>Holdings Breakdown</Typography>
                        {BREAKDOWN.map(h => (
                            <Box key={h.name} sx={{ mb:2.25, '&:last-child':{ mb:0 } }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={.75}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Box sx={{ width:8, height:8, borderRadius:'50%', background:h.color, flexShrink:0 }} />
                                        <Typography sx={{ color:textPri, fontWeight:600, fontSize:'.8rem' }}>{h.name}</Typography>
                                        <Box sx={{ px:.65, py:.15, borderRadius:5, background:alpha(isDark?'#fff':B.dark,.05) }}>
                                            <Typography sx={{ color:textFnt, fontSize:'.58rem', textTransform:'uppercase' }}>{h.type}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ textAlign:'right' }}>
                                        <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.8rem' }}>{fmt(h.value)}</Typography>
                                        <Typography sx={{ color:B.green, fontSize:'.68rem', fontWeight:700 }}>+{h.gain}%</Typography>
                                    </Box>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <LinearProgress variant="determinate" value={h.allocation} sx={{ flex:1, height:6, borderRadius:4, background:alpha(h.color,.1), '& .MuiLinearProgress-bar':{ background:`linear-gradient(90deg,${h.color},${alpha(h.color,.5)})`, borderRadius:4, transition:'transform 1s cubic-bezier(.16,1,.3,1)' } }} />
                                    <Typography sx={{ color:textMid, fontWeight:700, fontSize:'.72rem', minWidth:36, textAlign:'right' }}>{h.allocation}%</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Benchmarks */}
                <Box sx={{ ...glass, p:2.5, mb:2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <TrendingUpOutlined sx={{ color:B.purple, fontSize:18 }} />
                        <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.9rem' }}>Benchmark Comparison</Typography>
                    </Box>
                    <Typography sx={{ color:textMid, fontSize:'.72rem', mb:2 }}>Your 1-year portfolio return vs key benchmarks.</Typography>
                    {BENCHMARK_DATA.map((b, i) => {
                        const isPortfolio = b.label === 'Portfolio';
                        return (
                            <Box key={b.label} sx={{ mb: i < BENCHMARK_DATA.length - 1 ? 2 : 0 }}>
                                <Box display="flex" justifyContent="space-between" mb={.75}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Box sx={{ width:8, height:8, borderRadius:'50%', background:b.color }} />
                                        <Typography sx={{ color: isPortfolio ? textPri : textMid, fontWeight: isPortfolio ? 700 : 400, fontSize:'.78rem' }}>{b.label}</Typography>
                                        {isPortfolio && <Box sx={{ px:.65, py:.15, borderRadius:5, background:alpha(B.purple,.1) }}><Typography sx={{ color:B.purple, fontSize:'.58rem', fontWeight:700 }}>YOU</Typography></Box>}
                                    </Box>
                                    <Typography sx={{ color:b.color, fontWeight:700, fontSize:'.78rem' }}>{b.label==='Inflation'?`${b.value}%`:`+${b.value}%`}</Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={Math.min(b.value, 100)} sx={{ height: isPortfolio ? 8 : 5, borderRadius:4, background:alpha(b.color,.1), '& .MuiLinearProgress-bar':{ background:`linear-gradient(90deg,${b.color},${alpha(b.color,.6)})`, borderRadius:4, transition:'transform 1s cubic-bezier(.16,1,.3,1)' } }} />
                            </Box>
                        );
                    })}
                    <Box sx={{ mt:2, pt:1.5, borderTop:`1px solid ${divC}` }}>
                        <Typography sx={{ color:textFnt, fontSize:'.68rem' }}>
                            Your portfolio outperforms NGX ASI by <Typography component="span" sx={{ color:B.green, fontWeight:700, fontSize:'.68rem' }}>4.4pp</Typography> and beats T-Bills after inflation adjustment.
                        </Typography>
                    </Box>
                </Box>

                {/* Risk metrics */}
                <Box sx={{ ...glass, p:2.5, mb:2 }}>
                    <Typography sx={{ color:textPri, fontWeight:700, fontSize:'.9rem', mb:2 }}>Risk Metrics</Typography>
                    <Box sx={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:1.5 }}>
                        {[
                            { label:'Volatility',      value:'12.4%',  sub:'Moderate',  color:B.orange },
                            { label:'Sharpe Ratio',    value:'1.42',   sub:'Good',      color:B.green  },
                            { label:'Max Drawdown',    value:'-8.3%',  sub:'Low',       color:B.green  },
                            { label:'Beta vs NGX',     value:'0.78',   sub:'Defensive', color:B.blue   },
                        ].map(m => (
                            <Box key={m.label} sx={{ p:1.75, borderRadius:'14px', background:alpha(isDark?'#fff':B.dark,.04), border:`1px solid ${alpha(isDark?'#fff':B.dark,.05)}` }}>
                                <Typography sx={{ color:m.color, fontFamily:"'Instrument Serif',serif", fontSize:'1.3rem', lineHeight:1.2 }}>{m.value}</Typography>
                                <Typography sx={{ color:textPri, fontWeight:600, fontSize:'.75rem', mt:.4 }}>{m.label}</Typography>
                                <Typography sx={{ color:textFnt, fontSize:'.65rem' }}>{m.sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ mt:3.5, display:'flex', alignItems:'center', justifyContent:'center', gap:.75 }}>
                    <LockOutlined sx={{ fontSize:11, color:alpha(isDark?'#fff':B.ink,.18) }} />
                    <Typography sx={{ fontSize:'.62rem', color:alpha(isDark?'#fff':B.ink,.18), letterSpacing:'.05em' }}>256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES</Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default PortfolioAnalyticsPage;